import './_dashboardList.scss';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { DashboardT } from 'business/definitions';
import { DASHBOARD_SCOPE } from 'business/definitions';
import {
    addRecentAndFavoriteScopesContent,
    getIconFromScope,
    splitDashboardsByScope,
} from 'helpers/dashboardHelpers';
import { capitalize } from 'helpers/helpers';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLastViewedDashboards } from 'routes/Home/hooks/useGetLastViewedDashboards';

import { sortDashboardsbyTitle } from './helpers';

type Props = {
    dashboards: DashboardT[];
    homeDashboardSlug: string;
    dashboardItemRender: (arg1: DashboardT) => ReactNode;
};

const orderedDashboardScopes = [
    DASHBOARD_SCOPE.FAVORITE,
    DASHBOARD_SCOPE.RECENT,
    DASHBOARD_SCOPE.PRIVATE,
    DASHBOARD_SCOPE.SHARED,
    DASHBOARD_SCOPE.RESTRICTED,
    DASHBOARD_SCOPE.PUBLIC,
    DASHBOARD_SCOPE.TEMPLATE,
];

function DashboardList({
    dashboards,
    homeDashboardSlug,
    dashboardItemRender,
}: Props) {
    const { t } = useTranslation('components');
    const homeSlug = homeDashboardSlug;

    const { data: recentDashboards } = useGetLastViewedDashboards();

    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const sortedDashboards = sortDashboardsbyTitle(dashboards);
    const homeDashboard = sortedDashboards.find((d) => d.slug === homeSlug);
    const otherDashboards = sortedDashboards.filter(
        (d) =>
            d.slug !== homeDashboardSlug && d.slug !== currentDashboard?.slug,
    );

    const scopedDashboards = splitDashboardsByScope(otherDashboards);

    addRecentAndFavoriteScopesContent(
        scopedDashboards,
        recentDashboards,
        otherDashboards,
    );

    return (
        <div className="dashboards-list">
            {!!homeDashboard && dashboardItemRender(homeDashboard)}

            {/* Display the current dashboard separately */}
            {!!currentDashboard && (
                <div key="current">
                    <div className="dashboard-list-title">
                        <PinDropIcon />
                        <Typography variant="body1">
                            {capitalize(t('dashboards.currentDashboard'))}
                        </Typography>
                    </div>
                    {dashboardItemRender(currentDashboard)}
                </div>
            )}

            {dashboards.length !== 0 ? (
                orderedDashboardScopes
                    .filter((scope) => scopedDashboards[scope])
                    .map((scope) => (
                        <Accordion
                            key={scope}
                            defaultExpanded
                            disableGutters
                            sx={{
                                boxShadow: 'none',
                                '&:before': {
                                    display: 'none',
                                },
                                '&:last-child': {
                                    borderBottom: 'none',
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                },
                            }}
                            // disable transition animation to avoid losing time
                            slotProps={{
                                transition: {
                                    timeout: 0,
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <div className="dashboard-list-title">
                                    {getIconFromScope(scope)}
                                    <Typography variant="body1">
                                        {capitalize(t(`dashboards.${scope}`))}
                                    </Typography>
                                </div>
                            </AccordionSummary>

                            <AccordionDetails
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <div className="scope-list">
                                    {scopedDashboards[scope]?.map((dashboard) =>
                                        dashboardItemRender(dashboard),
                                    )}
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    ))
            ) : (
                <Typography>{t('dashboards.noDashboardFound')}</Typography>
            )}
        </div>
    );
}
export default DashboardList;
